import {activateNotification} from '../../../../../ui/molecules/notification/activate-notification';
import {
  ECheckinsFrequencyType,
  EQuestionType,
  ICreateCheckinCyclePayload,
  ICheckinCycleDetail,
  ICheckinCyclesResponse,
  ICheckinsPrioritiesResponseData,
  ICheckinsPulseResponseData,
  ICheckinsQuestionResponseData,
  ICreateQuestionForm,
  ICurrentCheckinsInfoResponseData,
  ISpecificCheckinsWithoutResponseData,
} from './company-checkins.interface';
import {removeEmptyStringFromObject} from '@utils/data-structure-algos';
import {
  CheckinListFilter,
  CheckinCyclesFilter,
  CompanyCheckinsRequest,
  CurrentCheckinFilter,
} from './company-checkins.request';
import {checkinName} from '@utils/checkin-name';

export class CompanyCheckinsController {
  constructor(private readonly request: CompanyCheckinsRequest) {}

  async createCheckinCycle(data: ICreateCheckinCyclePayload) {
    try {
      const response = await this.request.createCheckinCycle(data);
      activateNotification({
        title: 'Success',
        content: 'Check-in cycle created',
        kind: 'success',
      });
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async getCheckinCycles(
    filter?: CheckinCyclesFilter,
  ): Promise<ICheckinCyclesResponse | false> {
    try {
      const response = await this.request.getCheckinCycles(filter);
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async getCheckinCycle(cycleId: string): Promise<ICheckinCycleDetail | false> {
    try {
      const response = await this.request.getCheckinCycle(cycleId);
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async updateCheckinCycle(
    cycleId: string,
    data: Partial<ICreateCheckinCyclePayload>,
  ) {
    try {
      const response = await this.request.updateCheckinCycle(cycleId, data);
      activateNotification({
        title: 'Success',
        content: 'Check-in cycle updated',
        kind: 'success',
      });
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async deleteCheckinCycle(cycleId: string) {
    try {
      const response = await this.request.deleteCheckinCycle(cycleId);
      activateNotification({
        title: 'Success',
        content: 'Check-in cycle deleted',
        kind: 'success',
      });
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async createCheckinsQuestion(data: ICreateQuestionForm) {
    try {
      await this.request.createCheckinsQuestion(data);
      activateNotification({
        title: 'Success',
        content: `Question created`,
        kind: 'success',
      });
      return true;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }
  async resetCurrentCheckin() {
    try {
      await this.request.resetCurrentCheckin();

      activateNotification({
        title: 'Success',
        content: `Check-ins reset successful`,
        kind: 'success',
      });

      return true;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async getCheckinsQuestions(queryFilters?: {
    type?: EQuestionType;
    frequency?: ECheckinsFrequencyType;
    userQuestions?: boolean;
  }) {
    try {
      const response = await this.request.getCheckinsQuestions(queryFilters);
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async getSpecificCheckinsQuestion(questionId: string) {
    try {
      const response = await this.request.getSpecificCheckinsQuestion(
        questionId,
      );
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async updateCheckinQuestion(data: ICreateQuestionForm, questionId: string) {
    try {
      await this.request.updateCheckinQuestion(data, questionId);
      activateNotification({
        title: 'Success',
        content: `Question updated`,
        kind: 'success',
      });
      return true;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async deleteCheckinsQuestion(questionId: string) {
    try {
      await this.request.deleteCheckinsQuestion(questionId);
      activateNotification({
        title: 'Success',
        content: `Question deleted`,
        kind: 'success',
      });
      return true;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async getSpecificCheckinsBatch(batchId: string) {
    try {
      const response = await this.request.getSpecificCheckinsBatch(batchId);
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async updateCheckinBatch(data: ICreateQuestionForm, batchId: string) {
    try {
      await this.request.updateCheckinBatch(data, batchId);
      activateNotification({
        title: 'Success',
        content: `Questions updated`,
        kind: 'success',
      });
      return true;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async deleteCheckinsBatch(batchId: string) {
    try {
      await this.request.deleteCheckinsBatch(batchId);
      activateNotification({
        title: 'Success',
        content: `Questions deleted`,
        kind: 'success',
      });
      return true;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async getCurrentCheckinsInfo(
    filter?: CurrentCheckinFilter,
    hideError?: boolean,
  ): Promise<ICurrentCheckinsInfoResponseData[] | undefined> {
    try {
      const response = await this.request.getCurrentCheckinsInfo(filter);
      if (!response) {
        return [];
      }
      return Array.isArray(response) ? response : [response];
    } catch (e: any) {
      !hideError &&
        activateNotification({
          title: 'Error',
          content: e?.response?.data?.message,
          kind: 'error',
        });
      return;
    }
  }
  async getCurrentPulseInfo(
    filter?: {weekOfYear?: number; year?: number},
    hideError?: boolean,
  ) {
    try {
      const response = await this.request.getCurrentPulseInfo(filter);
      return response;
    } catch (e: any) {
      !hideError &&
        activateNotification({
          title: 'Error',
          content: e?.response?.data?.message,
          kind: 'error',
        });
      return;
    }
  }

  async getCheckinsQuestionsResponse(questionId: string) {
    try {
      const response = await this.request.getCheckinsQuestionsResponse(
        questionId,
      );
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async updateCheckinsQuestionsResponse(
    data: ICheckinsQuestionResponseData,
    questionId: string,
  ) {
    try {
      await this.request.updateCheckinsQuestionsResponse(data, questionId);

      return true;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async getCheckinsPulseResponse(pulseId: string) {
    try {
      const response = await this.request.getCheckinsPulseResponse(pulseId);
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async updateCheckinsPulseResponse(
    data: ICheckinsPulseResponseData,
    pulseId: string,
  ) {
    try {
      await this.request.updateCheckinsPulseResponse(data, pulseId);

      return true;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async getCheckinsPrioritiesResponse(prioritiesId: string) {
    try {
      const response = await this.request.getCheckinsPrioritiesResponse(
        prioritiesId,
      );
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async updateCheckinsPrioritiesResponse(
    data: ICheckinsPrioritiesResponseData,
    prioritiesId: string,
  ) {
    try {
      await this.request.updateCheckinsPrioritiesResponse(data, prioritiesId);

      return true;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async getListOfCheckins(filter?: CheckinListFilter, hideError = false) {
    try {
      const response = await this.request.getListOfCheckins(filter);
      return response;
    } catch (e: any) {
      if (!hideError) {
        activateNotification({
          title: 'Error',
          content: e?.response?.data?.message,
          kind: 'error',
        });
      }
      return false;
    }
  }
  async getPulses(filter?: {
    weekOfYear?: number;
    year?: number;
    isActive?: boolean;
    isSubmitted?: boolean;
    isReviewed?: boolean;
    reviewer?: string;
    manager?: string;
    user?: string;
    page?: number;
  }) {
    try {
      const response = await this.request.getPulses(filter);
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async getCheckinById(checkinId: string) {
    try {
      const response = await this.request.getCheckinById(checkinId);
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async updateCheckinById(
    data: ISpecificCheckinsWithoutResponseData,
    checkinId: string,
  ) {
    try {
      await this.request.updateCheckinById(
        removeEmptyStringFromObject(data),
        checkinId,
      );
      return true;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async getCheckinByIdAndFetchResponses(checkinId: string) {
    try {
      const response = await this.request.getCheckinByIdAndFetchResponses(
        checkinId,
      );
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async submitSpecificCheckin(
    data: ISpecificCheckinsWithoutResponseData,
    checkinId: string,
    type?: 'update' | 'submit' | 'report',
  ) {
    try {
      await this.request.submitSpecificCheckin(data, checkinId);
      const label = checkinName();
      const sentenceCaseLabel = label
        ? `${label.charAt(0).toUpperCase()}${label.slice(1)}`
        : 'Check-in';
      activateNotification({
        title: 'Success',
        content: `${sentenceCaseLabel} ${
          type === 'update'
            ? 'updated'
            : type === 'report'
            ? 'reviewed'
            : 'submitted'
        }`,
        kind: 'success',
      });
      return true;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async getCheckinsCalendarWeekList(filter?: {
    manager?: string;
    user?: string;
  }) {
    try {
      const response = await this.request.getCheckinsCalendarWeekList(filter);
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }
  async checkinReminder() {
    try {
      const response = await this.request.checkinReminder();
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async sendCheckinReminder(checkinId: string) {
    try {
      const response = await this.request.sendCheckinReminder(checkinId);
      activateNotification({
        title: 'Success',
        content: 'Reminder sent successfully',
        kind: 'success',
      });
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }

  async sendCheckinReminders(checkinIds: string[]) {
    const batchSize = 5;
    let successCount = 0;
    let failedCount = 0;
    const successfulIds: string[] = [];
    const failedIds: string[] = [];

    for (let index = 0; index < checkinIds.length; index += batchSize) {
      const batch = checkinIds.slice(index, index + batchSize);
      const results = await Promise.allSettled(
        batch.map((checkinId) => this.request.sendCheckinReminder(checkinId)),
      );
      for (
        let resultIndex = 0;
        resultIndex < results.length;
        resultIndex += 1
      ) {
        const result = results[resultIndex];
        const checkinId = batch[resultIndex];
        if (result.status === 'fulfilled') {
          successCount += 1;
          successfulIds.push(checkinId);
        } else {
          failedCount += 1;
          failedIds.push(checkinId);
        }
      }
    }

    if (failedCount === 0) {
      activateNotification({
        title: 'Success',
        content: `Reminder${
          successCount === 1 ? '' : 's'
        } sent to ${successCount} participant${successCount === 1 ? '' : 's'}`,
        kind: 'success',
      });
    } else {
      activateNotification({
        title: successCount ? 'Some reminders were not sent' : 'Error',
        content: successCount
          ? `${successCount} sent; ${failedCount} failed`
          : 'Unable to send reminders',
        kind: successCount ? 'warning' : 'error',
      });
    }

    return {successCount, failedCount, successfulIds, failedIds};
  }

  async getCheckinsQuestionsTemplates(filter?: {
    type?: string;
    getTypes?: string;
  }) {
    try {
      const response = await this.request.getCheckinsQuestionsTemplates(filter);
      return response;
    } catch (e: any) {
      activateNotification({
        title: 'Error',
        content: e?.response?.data?.message,
        kind: 'error',
      });
      return false;
    }
  }
}
